import React, { useState, useEffect } from "react";

interface UnitDropDownProps {
  label: string;
  errors: any;
  placeholder?: string;
  unitOptions?: string[];
  selectedUnit?: string;
  selectedValue?: string | number;
  onUnitChange?: (unit: string) => void;
  onValueChange?: (value: string) => void;
  name: string;
  unitName?: string; // Name for the unit field in form
  register: any;
  setValue?: any; // For setting unit value in react-hook-form
  required?: boolean;
  className?: string;
  disabled?: boolean;
  onBlur?: () => void;
}

const UnitDropDown: React.FC<UnitDropDownProps> = ({
  label,
  errors,
  placeholder = "Enter value",
  unitOptions = ["Unit", "Kg", "Lbs"],
  selectedUnit = "Unit",
  selectedValue = "",
  onUnitChange,
  onValueChange,
  name,
  unitName = `${name}_unit`,
  register,
  setValue,
  required = true,
  className = "",
  disabled = false,
  onBlur,
}) => {
  const [unit, setUnit] = useState(selectedUnit);
  const [value, setValue_local] = useState(String(selectedValue));

  // Handle initial values and updates for edit mode
  useEffect(() => {
    if (selectedUnit && selectedUnit !== unit) {
      setUnit(selectedUnit);
    }
  }, [selectedUnit]);

  useEffect(() => {
    if (selectedValue !== undefined && String(selectedValue) !== value) {
      setValue_local(String(selectedValue));
    }
  }, [selectedValue]);

  // Update unit in form when unit changes
  useEffect(() => {
    if (setValue && unitName) {
      setValue(unitName, unit);
    }
  }, [unit, setValue, unitName]);

  // Register the unit field
  const unitRegister = unitName ? register(unitName) : {};

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove any non-numeric characters except for decimal point
    const numericValue = e.target.value.replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const decimalValue = numericValue.split('.').slice(0, 2).join('.');
    
    setValue_local(decimalValue);
    
    if (onValueChange) onValueChange(decimalValue);
  };

  const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newUnit = e.target.value;
    setUnit(newUnit);
    
    if (onUnitChange) onUnitChange(newUnit);
    
    // This hidden input will be updated with the current unit value
    if (unitRegister.onChange) {
      const fakeEvent = {
        target: { value: newUnit, name: unitName }
      } as React.ChangeEvent<HTMLInputElement>;
      unitRegister.onChange(fakeEvent);
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Label */}
      <label htmlFor={name} className="input-label block text-sm font-medium text-gray-700 mb-1">
        {label ? label : <>&nbsp;</>}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {/* Hidden input for unit value */}
      <input
        type="hidden"
        {...unitRegister}
        value={unit}
      />

      {/* Input and Dropdown */}
      <div className="flex rounded-lg overflow-hidden shadow-sm relative w-full">
        <input
          id={name}
          type="number"
          placeholder={placeholder}
          className={`flex-1 p-2 px-3 border rounded-l-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none w-full
            ${errors[name] ? "border-red-500" : "border-gray-300"}
            ${disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-white"}`}
          disabled={disabled}
          value={value}
          {...register(name, {
            required: required ? "This field is required" : false,
            onChange: handleValueChange,
            // Convert to number in the register validation
            setValueAs: (v: string) => v !== '' ? Number(v) : undefined
          })}
          onBlur={onBlur}
        />
        
        <select
          value={unit}
          onChange={handleUnitChange}
          disabled={disabled}
          className={`bg-gray-50 border-t border-r border-b px-3 py-2 text-sm rounded-r-lg 
            ${disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "cursor-pointer hover:bg-gray-100"}
            ${errors[unitName] ? "border-red-500" : "border-gray-300"}
            focus:outline-none focus:ring-2 focus:ring-blue-500`}
          aria-label={`Unit for ${label}`}
        >
          {unitOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>

      {/* Error Messages */}
      {errors[name] && (
        <p className="text-red-500 text-xs mt-1">
          {errors[name]?.message}
        </p>
      )}
      {errors[unitName] && (
        <p className="text-red-500 text-xs mt-1">
          {errors[unitName]?.message}
        </p>
      )}
    </div>
  );
};

export default UnitDropDown;